import { DataType } from "../../evaluators/DataType";
import { EvaluationResult } from "../../evaluators/EvaluationResult";
import { Evaluator } from "../../evaluators/Evaluator";
import { BinaryExpression } from "../../expressions/BinaryExpression";
import { ConstantExpression } from "../../expressions/ConstantExpression";
import { FunctionExpression } from "../../expressions/FunctionExpression";
import { GroupExpression } from "../../expressions/GroupExpression";
import { RJExpressionNode } from "../../expressions/RJExpressionNode";
import { RJExpressionNodeType } from "../../expressions/RJExpressionNodeType";
import { SequenceExpression } from "../../expressions/SequenceExpression";
import { UnaryExpression } from "../../expressions/UnaryExpression";
import { VariableExpression } from "../../expressions/VariableExpression";
import { Logical } from "./Library/Logical";
import { Trigonemtry } from "./Library/Trigonemetry";
import { DataTypes } from "./Library/DataTypes";
import { LomaFunction } from "./LomaFunction";
import { LomaVariable } from "./LomaVariable";
import { Types } from "./Library/Types";
import { Multiply } from "./Library/Operators/Multiply";
import { Minus } from "./Library/Operators/Minus";
import { Plus } from "./Library/Operators/Plus";
import { Division } from "./Library/Operators/Division";
import { Power } from "./Library/Operators/Power";
import { Modulo } from "./Library/Operators/Modulo";
import { RJParserMessages } from "../RJParserMessages";
import { Vector3 } from "three";
import { Exponentation } from "./Library/Exponentiation";
import { Vectors } from "./Library/Vectors";
import { Rounding } from "./Library/Rounding";

export class LomaEvaluator extends Evaluator
{
  _functions:Map<string,LomaFunction[]> = new Map<string,LomaFunction[]>();
  _variables:Map<string,LomaVariable<any>> = new Map<string,LomaVariable<any>>();
  
  _plus = new Plus();
  _minus = new Minus();
  
  _multiply = new Multiply();
  _division = new Division();
  _power    = new Power();
  _modulo   = new Modulo();

  constructor()
  {
    super();

    this._addFunctions( Trigonemtry.functions );
    this._addVariables( Trigonemtry.variables );
    this._addVariables( Exponentation.variables );
 
    this._addFunctions( Logical.functions );
    this._addFunctions( Types.functions );
    this._addFunctions( Exponentation.functions );
    this._addFunctions( Vectors.functions );
    this._addFunctions( Rounding.functions );
  }


  _addFunctions( functions:LomaFunction[] )
  {
    functions.forEach( 
      f => 
      {
        if ( ! this._functions.has( f.functionName ) )
        {
          this._functions.set( f.functionName, [] );
        }

        this._functions.get( f.functionName ).push( f ); 
      }
    );
  }

  _addVariables( variables:LomaVariable<any>[] )
  {
    variables.forEach( v => this._variables.set( v.variableName, v ) );
  }

  _evaluateNode( expression:RJExpressionNode ):EvaluationResult<any>
  {
    switch ( expression.type )
    {
      case RJExpressionNodeType.BINARY: return this._evaluateBinary( expression );
      case RJExpressionNodeType.CONSTANT: return this._evaluateConstant( expression );
      case RJExpressionNodeType.GROUP: return this._evaluateGroup( expression );
      case RJExpressionNodeType.UNARY: return this._evaluateUnary( expression );
      case RJExpressionNodeType.FUNCTION: return this._evaluateFunction( expression );
      case RJExpressionNodeType.VARIABLE: return this._evaluateVariable( expression );
    }

    return EvaluationResult.asError( expression, "Invalid Expression for Evaluation: " + RJExpressionNodeType[ expression.type ] );
  }

  _evaluateConstant( expression:RJExpressionNode ):EvaluationResult<any>
  {
    let constExpression = expression as ConstantExpression<number>;
    return EvaluationResult.withValue( constExpression.value, DataTypes.Number );
  }

  _evaluateGroup( expression:RJExpressionNode ):EvaluationResult<any>
  {
    let groupExpression = expression as GroupExpression;
    return this._evaluateNode( groupExpression._expression );
  }
  
  _evaluateFunction( expression:RJExpressionNode ):EvaluationResult<any>
  {
    let functionExpression = expression as FunctionExpression;

    let name = functionExpression._functionName.match;

    if ( ! this._functions.has( name ) )
    {
      return EvaluationResult.asError( expression, "Function not found: " + name );
    }

    let values:EvaluationResult<any>[] = [];
    
    let groupExpression = functionExpression._functionGroup._expression;

    if ( RJExpressionNodeType.SEQUENCE === groupExpression.type )
    {
      let sequenceExpression = groupExpression as SequenceExpression;

      for ( let i = 0; i < sequenceExpression._expressions.length; i++ )
      {
        let result = this._evaluateNode( sequenceExpression._expressions[ i ] );

        if ( ! result.success )
        {
          return result;
        }

        values.push( result );
      }
    }
    else
    {
      values = [ this._evaluateNode( groupExpression ) ];

      if ( ! values[ 0 ].success )
      {
        return values[ 0 ];
      }
    }

    let allFunctions = this._functions.get( name );
    let func:LomaFunction = null;
    
    for ( let f of allFunctions )
    {
      let parametersFitting = f.checkParameters( values );

      if ( parametersFitting )
      {
        func = f;
        break;
      }
    }

    if ( func === null )
    {
      return EvaluationResult.asError( expression, "No function found with with parameters for:" + name );
    }

    return func.callFunction( this, functionExpression, values );
  }

  _evaluateVariable( expression:RJExpressionNode ):EvaluationResult<any>
  {
    let variableExpression = expression as VariableExpression;

    let name = variableExpression._variableName.match;

    if ( ! this._variables.has( name ) )
    {
      return EvaluationResult.asError( expression, "Variable not found: " + name );
    }

    let variable = this._variables.get( name );

    return EvaluationResult.withValue( variable.value, DataTypes.Number );
  }
  
  _evaluateBinary( expression:RJExpressionNode ):EvaluationResult<any>
  {
    let binary = expression as BinaryExpression;
    
    let left  = this._evaluateNode( binary.left )  as EvaluationResult<number>;
    let right = this._evaluateNode( binary.right ) as EvaluationResult<number>;

    if ( ! left.success )
    {
      return left;
    }

    if ( ! right.success )
    {
      return right;
    }


    let value = 0;

    switch ( binary.operatorToken.match )
    {
      case "*": return this._multiply.process( this, binary, [ left, right ] );
      case "+": return this._plus.process( this, binary, [ left, right ] );
      case "-": return this._minus.process( this, binary, [ left, right ] );
      case "/": return this._division.process( this, binary, [ left, right ] );
      case "^": return this._power.process( this, binary, [ left, right ] );
      case "%": return this._modulo.process( this, binary, [ left, right ] );
    }

    return EvaluationResult.withValue( value, DataTypes.Number );
    
  }

  _evaluateUnary( expression:RJExpressionNode ):EvaluationResult<any>
  {
    let unary = expression as UnaryExpression;
    
    let unaryExpressionResult  = this._evaluateNode( unary._expression )  as EvaluationResult<any>;

    if ( ! unaryExpressionResult.success )
    {
      return unaryExpressionResult;
    }

    let minus = unary.operatorToken.match === "-";

    if ( ! minus )
    {
      return unaryExpressionResult;
    } 

    let rawValue = unaryExpressionResult.data;

    if ( DataTypes.Number === unaryExpressionResult.type )
    {
      let value = rawValue as number;
      return EvaluationResult.withValue( -value, DataTypes.Number );
    }
    else ( DataTypes.Vector3 === unaryExpressionResult.type )
    {
      let value = rawValue as Vector3;
      return EvaluationResult.withValue( value.clone().negate(), DataTypes.Vector3 );
    }    

    return EvaluationResult.asError( unary, RJParserMessages.parametersNotFitting );

  }

  
}